import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { DockHost } from "@/components/shared/whatsapp/dock-host";
import { closeDockIfOpen } from "@/components/shared/whatsapp/dock-store";
import { cn } from "@/lib/utils";

/**
 * Side panel. Used here for the mobile navigation drawer; later for row
 * quick-view per the skill's overlay rules. Never nest one inside another.
 *
 * Every open sheet carries a `DockHost`: the docked WhatsApp panel renders
 * inside the newest one, so it stays usable over a modal sheet (focus trap,
 * scroll lock and aria-hidden would otherwise shut it out). Escape closes that
 * panel first and the sheet only after.
 */
const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;
const SheetTitle = SheetPrimitive.Title;
const SheetDescription = SheetPrimitive.Description;

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> & {
    side?: "left" | "right";
    /** False when the sheet draws its own Close button in its header. */
    showClose?: boolean;
  }
>(
  (
    { className, children, side = "right", showClose = true, onEscapeKeyDown, ...props },
    ref,
  ) => (
    <SheetPrimitive.Portal>
      <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-foreground/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(
          "fixed z-50 flex h-full w-[280px] max-w-[85vw] flex-col gap-4 bg-card p-4 shadow-lg transition ease-in-out data-[state=closed]:duration-200 data-[state=open]:duration-200",
          side === "left"
            ? "inset-y-0 left-0 border-r border-border data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left"
            : "inset-y-0 right-0 border-l border-border data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          className,
        )}
        onEscapeKeyDown={(event) => {
          onEscapeKeyDown?.(event);
          if (!event.defaultPrevented && closeDockIfOpen()) event.preventDefault();
        }}
        {...props}
      >
        {children}
        <DockHost />
        {showClose ? (
          <SheetPrimitive.Close className="absolute right-4 top-4 cursor-pointer rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            <X className="size-4" aria-hidden="true" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        ) : null}
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  ),
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetTitle, SheetDescription };
